/** O que a API da folha devolve, e os formatos que as telas dela usam. */

export type FolhaDaLista = {
  id: number;
  competencia: string;
  rotulo: string;
  status: 'rascunho' | 'conferida' | 'exportada';
  emissao: string | null;
  processado_em: string | null;
  arquivo_nome: string | null;
  importado_em: string | null;
  linhas: number;
  total: number;
  empresa: { proventos: number; descontos: number; liquido: number; funcionarios: number };
  /** O que o escritório manda depois da folha: um relatório por setor e o totalizador. */
  impostos: { setores: number; com_relatorio: number; totalizador: boolean };
};

export type SetorDaFolha = {
  id: number;
  codigo: string;
  nome: string;
  centro_custo: string | null;
  centro_custo_id: number | null;
  proventos: number;
  descontos: number;
  liquido: number;
  funcionarios: number;
  relatorio_recebido: boolean;
  relatorio: RelatorioRecebido | null;
};

/** O arquivo do escritório que vale para o setor, e quando chegou. */
export type RelatorioRecebido = { arquivo: string | null; recebido_em: string };

/** O que veio num relatório, campo a campo, ao lado do que o PDF da folha trazia. */
export type DadosDoRelatorio = {
  nome: string;
  arquivo: string | null;
  recebido_em: string | null;
  campos: { campo: string; rotulo: string; relatorio: number; pdf: number | null }[];
};

/** O resultado de cada arquivo enviado na tela de impostos. */
export type EnvioDeRelatorios = {
  recebidos: {
    arquivo: string;
    tipo: 'setor' | 'totalizador';
    setor: string | null;
    setor_id: number | null;
    campos: number;
    substituiu: string | null;
    avisos: string[];
  }[];
  recusados: { arquivo: string; motivo: string }[];
  mudancas: MudancaDeLancamento[];
};

/** Um lançamento que mudou de valor ao receber ou remover um relatório; nulo é linha que não existia. */
export type MudancaDeLancamento = { historico: string; antes: number | null; depois: number | null };

export type DetalheDaFolha = {
  id: number;
  competencia: string;
  rotulo: string;
  status: FolhaDaLista['status'];
  emissao: string | null;
  processado_em: string | null;
  arquivo_nome: string | null;
  lancamentos: number;
  totalizador_recebido: boolean;
  totalizador: RelatorioRecebido | null;
  setores: SetorDaFolha[];
  empresa: { proventos: number; descontos: number; liquido: number; funcionarios: number };
  rubricas_pendentes: { id: number; codigo: string; descricao: string }[];
};

export type Checagem = {
  chave: string;
  titulo: string;
  situacao: 'ok' | 'atencao' | 'erro';
  detalhe: string;
  problemas: string[];
};

export type Conferencia = { aprovada: boolean; checagens: Checagem[] };

export type Lancamento = {
  id: number;
  ordem: number;
  verba: string;
  centro_custo: string;
  conta_debito: string;
  conta_credito: string;
  valor: number;
  historico: string;
  origem: 'layout' | 'nova';
};

export type Vinculos = {
  setores: { id: number; codigo: string; nome: string; centro_custo_id: number | null; centro_custo: string | null }[];
  rubricas: {
    id: number;
    codigo: string;
    descricao: string;
    tipo: 'provento' | 'desconto' | 'informativo';
    folha_verba_id: number | null;
    verba: string | null;
    ignorar: boolean;
    motivo: string | null;
    classificada: boolean;
    /** Falso para a rubrica cadastrada antes de o escritório mandá-la. */
    na_folha: boolean;
  }[];
  centros_custo: { id: number; nome: string; apelido: string }[];
  verbas: { id: number; nome: string; origem: string; colunas: string; conta_debito: string; conta_credito: string }[];
};

/** A conta de uma linha da planilha: as rubricas de cada setor que caem nela. */
export type Composicao = {
  verba: string;
  centro_custo: string;
  valor: number;
  regra: string;
  total: number;
  setores: { codigo: string; nome: string; itens: { rotulo: string; valor: number }[]; total: number }[];
};

export type ColunasDaVerba = 'total' | 'ativos_outros';

export type ContaDoCentro = { centro_custo_id: number; conta_debito: string; conta_credito: string };

export type Verba = {
  id: number;
  nome: string;
  origem: 'rubricas' | 'rescisao' | 'tributo' | 'totalizador';
  colunas: ColunasDaVerba | 'desligados';
  conta_debito: string;
  conta_credito: string;
  regra: string;
  linhas: number;
  rubricas: { codigo: string; descricao: string }[];
  contas: (ContaDoCentro & { centro_custo: string | null })[];
  /** Por que não pode sair do cadastro; nulo quando nada a usa. */
  impede_exclusao: string | null;
};

export type Verbas = { verbas: Verba[]; centros_custo: { id: number; nome: string; ativo: boolean }[] };

export { erroDaApi } from '@/lib/erroDaApi';

export const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export const SITUACAO: Record<FolhaDaLista['status'], { rotulo: string; classe: string }> = {
  rascunho: { rotulo: 'Em conferência', classe: 'bg-warning/15 text-warning' },
  conferida: { rotulo: 'Conferida', classe: 'bg-accent/15 text-accent' },
  exportada: { rotulo: 'Enviada ao ERP', classe: 'bg-success/15 text-success' },
};

/** Só a primeira letra: a classe capitalize do CSS faria "Julho De 2026". */
export function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** "2026-07-01" vira "julho de 2026", como o escritório nomeia a competência. */
export function competenciaPorExtenso(data: string): string {
  const [ano, mes] = data.split('-');
  const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  return `${nomes[Number(mes) - 1]} de ${ano}`;
}
